// HazardMap.jsx — main interactive map: hazard heat layer + collateral pins
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, FormControl, Select, MenuItem, InputLabel, Chip, Button,
  CircularProgress, Tooltip,
} from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, GeoJSON, Popup, LayersControl } from "react-leaflet";
import L from "leaflet";
import { Layers as LayersIcon, Filter, MapPin } from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { hazardsApi, riskApi } from "../api/api";
import {
  fmtINR, fmtScore, HAZARD_LABELS, HAZARD_COLORS, riskColor,
} from "../utils/format";

// Severity 1-5 to color for hazard zone
const SEV_COLOR = ["#F3F4F6", "#E2D7B5", "#D9B26F", "#C77A4C", "#B04B3F", "#7A2A20"];

export default function HazardMap() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState("current");
  const [hazardId, setHazardId] = useState("flood");
  const [hazardList, setHazardList] = useState([]);
  const [zones, setZones] = useState(null);
  const [collaterals, setCollaterals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hazardsApi.list().then(({ data }) => {
      setHazardList(data.hazards || []);
      if ((data.hazards || []).length && !hazardId) setHazardId(data.hazards[0].hazard_id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!hazardId) return;
    hazardsApi.zones(hazardId, scenario).then(({ data }) => setZones(data));
  }, [hazardId, scenario]);

  useEffect(() => {
    setLoading(true);
    riskApi.cachedPortfolio(scenario)
      .then(({ data }) => setCollaterals(data.results || []))
      .finally(() => setLoading(false));
  }, [scenario]);

  const zoneStyle = (feature) => {
    const sev = feature.properties.severity || 0;
    return {
      color: SEV_COLOR[Math.min(5, sev)],
      fillColor: SEV_COLOR[Math.min(5, sev)],
      fillOpacity: 0.35,
      weight: 1,
    };
  };

  const summary = useMemo(() => {
    let high = 0, total = 0, fin = 0;
    collaterals.forEach((c) => {
      total++;
      fin += c.financial_exposure_inr || 0;
      if (c.risk_level === "High" || c.risk_level === "Critical") high++;
    });
    return { total, high, fin };
  }, [collaterals]);

  return (
    <DashboardLayout
      title="Hazard Map"
      subtitle={`${HAZARD_LABELS[hazardId] || hazardId} · ${scenario.toUpperCase()}`}
      headerExtra={<ScenarioToggle value={scenario} onChange={setScenario} />}
    >
      {/* Filter bar */}
      <Paper sx={{ p: 1.4, mb: 2, display: "flex", gap: 1.4, alignItems: "center", flexWrap: "wrap" }} data-testid="map-filters">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#6B7280", fontSize: 11.5, fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          <Filter size={13} /> Hazard layer
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Hazard</InputLabel>
          <Select label="Hazard" value={hazardId} onChange={(e) => setHazardId(e.target.value)}
            inputProps={{ "data-testid": "map-hazard-select" }}>
            {hazardList.map((h) => (
              <MenuItem key={h.hazard_id} value={h.hazard_id}>{HAZARD_LABELS[h.hazard_id] || h.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label={`${summary.total} collaterals`} data-testid="map-coll-count" />
        <Chip size="small" label={`${summary.high} high-risk`} sx={{ background: "#FCE8E6", color: "#A93226", fontWeight: 600 }} />
        <Chip size="small" label={fmtINR(summary.fin)} sx={{ background: "#F0EFEA", fontWeight: 600 }} />
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 2 }}>
        <Paper sx={{ p: 0, overflow: "hidden", height: 620 }} data-testid="hazard-map-container">
          <MapContainer center={[22.5, 80]} zoom={5} style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true} maxBounds={[[5, 65], [38, 100]]} minZoom={4}>
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="CARTO Positron (Light)">
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap, &copy; CARTO'
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="CARTO Voyager">
                <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
              </LayersControl.BaseLayer>
              <LayersControl.Overlay checked name={`${HAZARD_LABELS[hazardId] || hazardId} severity (${scenario})`}>
                <GeoJSONLayer zones={zones} style={zoneStyle} />
              </LayersControl.Overlay>
            </LayersControl>

            {collaterals.filter((c) => c.latitude && c.longitude).map((c) => (
              <CircleMarker
                key={c.collateral_id}
                center={[c.latitude, c.longitude]}
                radius={8 + (Math.log10((c.current_valuation_inr || 1) / 1e7 + 1) * 4)}
                pathOptions={{
                  color: riskColor(c.risk_level || "Low"),
                  fillColor: riskColor(c.risk_level || "Low"),
                  fillOpacity: 0.85,
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ minWidth: 220, fontFamily: "IBM Plex Sans, sans-serif", fontSize: 12.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 3 }}>{c.collateral_name}</div>
                    <div style={{ color: "#4B5563", marginBottom: 6 }}>
                      {c.borrower_name} · <span style={{ textTransform: "uppercase" }}>{c.borrower_portfolio}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px", fontFamily: "IBM Plex Mono, monospace" }}>
                      <div style={{ color: "#6B7280" }}>Score</div><div>{fmtScore(c.overall_score)} · {c.risk_level}</div>
                      <div style={{ color: "#6B7280" }}>Top hazard</div><div>{HAZARD_LABELS[c.top_hazard] || c.top_hazard || "—"}</div>
                      <div style={{ color: "#6B7280" }}>Exposure</div><div>{fmtINR(c.financial_exposure_inr)}</div>
                      <div style={{ color: "#6B7280" }}>Value</div><div>{fmtINR(c.current_valuation_inr)}</div>
                      <div style={{ color: "#6B7280" }}>PIN</div><div>{c.pin_code || "—"}</div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <a href={`/collaterals/${c.collateral_id}`}
                        style={{ color: "#2E5A35", fontWeight: 600, textDecoration: "none" }}>
                        Open detail →
                      </a>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </Paper>

        {/* Side panel: legend + top assets */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper sx={{ p: 2 }} data-testid="map-legend">
            <Box className="cb-display" sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 1 }}>
              <LayersIcon size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Severity legend
            </Box>
            {[5, 4, 3, 2, 1].map((sev) => (
              <Box key={sev} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
                <Box sx={{ width: 14, height: 14, background: SEV_COLOR[sev], border: "1px solid #CBD5E1" }} />
                <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#4B5563" }}>
                  {sev} — {["Negligible", "Low", "Moderate", "Significant", "Severe", "Extreme"][sev]}
                </Box>
              </Box>
            ))}
            <Box sx={{ borderTop: "1px solid #E2E8F0", mt: 1.4, pt: 1.4 }}>
              <Box className="cb-kpi-label" sx={{ mb: 0.6 }}>Pin colour (Risk Level)</Box>
              {["Critical", "High", "Medium", "Low"].map((lvl) => (
                <Box key={lvl} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.3 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: riskColor(lvl), border: "1.5px solid #FFFFFF", boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }} />
                  <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#4B5563" }}>{lvl}</Box>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper sx={{ p: 0, overflow: "hidden", flex: 1 }} data-testid="map-top-list">
            <Box sx={{ p: 1.6, borderBottom: "1px solid #F0EFEA" }}>
              <Box className="cb-display" sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Top by risk</Box>
            </Box>
            <Box sx={{ maxHeight: 460, overflowY: "auto" }}>
              {loading ? (
                <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={20} /></Box>
              ) : collaterals.slice(0, 12).map((c) => (
                <Box key={c.collateral_id}
                  onClick={() => navigate(`/collaterals/${c.collateral_id}`)}
                  data-testid={`map-side-${c.collateral_id}`}
                  sx={{
                    p: 1.4, borderBottom: "1px solid #F0EFEA", cursor: "pointer",
                    "&:hover": { background: "#F7F7F6" },
                  }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.collateral_name}
                      </Box>
                      <Box sx={{ fontSize: 11, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <MapPin size={9} style={{ verticalAlign: "middle", marginRight: 3 }} />
                        {c.city || c.state || "—"}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right", ml: 1 }}>
                      <Box className="cb-mono" sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{fmtScore(c.overall_score)}</Box>
                      <RiskPill level={c.risk_level} />
                    </Box>
                  </Box>
                </Box>
              ))}
              {!loading && collaterals.length === 0 && (
                <Box sx={{ p: 3, fontSize: 12, color: "#94A3B8", textAlign: "center" }}>
                  No scored collaterals yet. Re-score from Dashboard.
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

// Helper: GeoJSON layer that re-renders when data changes
function GeoJSONLayer({ zones, style }) {
  const [key, setKey] = useState(0);
  useEffect(() => { setKey((k) => k + 1); }, [zones]);
  if (!zones) return null;
  return (
    <GeoJSON
      key={key}
      data={zones}
      style={style}
      onEachFeature={(feature, layer) => {
        const p = feature.properties || {};
        layer.bindTooltip(
          `<b>${p.state}</b><br/>${HAZARD_LABELS[p.hazard_id] || p.hazard_id} · severity ${p.severity}/5 · ${p.scenario}`,
          { sticky: true }
        );
      }}
    />
  );
}
